import { createDraggable } from '@neodrag/solid';
import { animate } from 'motion';
import { For, JSX, onCleanup, onMount } from 'solid-js';
import { createStore } from 'solid-js/store';
import { Portal } from 'solid-js/web';

import { BarLoading } from './Loading';

export interface ContentItem {
  id: number;
  content: () => string | JSX.Element;
  header?: () => string | JSX.Element;
  footer?: () => string | JSX.Element;
  top?: number; // ✅ 新增：自定义 Y 坐标
  left?: number; // ✅ 新增：自定义 X 坐标
  closeCallback?: (id: number) => void;
  ready?: Promise<unknown> | (() => Promise<unknown>);
  zoomed?: boolean;
  state?: 'visible' | 'hidden';
  isNeedZoomed?: boolean; // ✅ 新增：是否需要显示缩放按钮（默认 true）
}

const DIALOG_WIDTH = 360;
const DIALOG_HEIGHT = 240;
const DIALOG_ZOOM_WIDTH = 800;
const DIALOG_ZOOM_HEIGHT = 538;
const FIXED_RIGHT = 88;
const FIXED_BOTTOM = 80;
// const SPACING = 20;

let toastId = 0;

export default function LivingDialog() {
  const [content, setContent] = createStore<(ContentItem & { loading: boolean })[]>([]);
  const animatedDialogs = new Set<number>(); // 记录已经动画过的弹窗 ID

  // 库的用法就是这样，忽略 eslint 警告
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { draggable } = createDraggable();
  const getDialogSize = (zoomed: boolean) =>
    zoomed ? { width: DIALOG_ZOOM_WIDTH, height: DIALOG_ZOOM_HEIGHT } : { width: DIALOG_WIDTH, height: DIALOG_HEIGHT };
  const getViewport = () =>
    typeof window === 'undefined' ? { width: 0, height: 0 } : { width: window.innerWidth, height: window.innerHeight };
  const getTranslate = (element: HTMLElement) => {
    const transform = window.getComputedStyle(element).transform;
    console.log('transform', transform);
    if (!transform || transform === 'none') return { x: 0, y: 0 };
    const matrix3d = transform.match(/^matrix3d\((.+)\)$/);
    if (matrix3d) {
      const values = matrix3d[1].split(',').map((v) => Number.parseFloat(v.trim()));
      return { x: values[12] || 0, y: values[13] || 0 };
    }
    const matrix2d = transform.match(/^matrix\((.+)\)$/);
    if (matrix2d) {
      const values = matrix2d[1].split(',').map((v) => Number.parseFloat(v.trim()));
      return { x: values[4] || 0, y: values[5] || 0 };
    }
    return { x: 0, y: 0 };
  };
  const clampDialogIntoViewport = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const view = getViewport();
    let deltaX = 0;
    let deltaY = 0;
    console.log('rect', rect);
    if (rect.left < 0) deltaX = -rect.left;
    if (rect.right > view.width) deltaX = view.width - rect.right;
    if (rect.top < 0) deltaY = -rect.top;
    if (rect.bottom > view.height) deltaY = view.height - rect.bottom;
    if (deltaX === 0 && deltaY === 0) return;
    const current = getTranslate(element);
    console.log('current', current);
    element.style.transform = `translate(${current.x + deltaX}px, ${current.y + deltaY}px)`;
  };
  const clampAllDialogs = () => {
    if (typeof window === 'undefined') return;
    const dialogs = document.querySelectorAll<HTMLElement>('[id^="living-dialog-"]');
    dialogs.forEach((dialog) => clampDialogIntoViewport(dialog));
  };
  const updateViewport = () => {
    requestAnimationFrame(clampAllDialogs);
  };
  // 弹窗动画
  const playSlideInAnimation = (element: HTMLDivElement, dialogId: number) => {
    // 防止重复动画
    if (animatedDialogs.has(dialogId)) return;
    animatedDialogs.add(dialogId);

    element.style.transformOrigin = 'center';
    animate(
      element,
      {
        transform: ['scale(0.5)', 'scale(1)'],
        opacity: [0, 1],
      } as any,
      {
        duration: 0.15,
        easing: [0.25, 0.46, 0.45, 0.94],
      } as any
    );
  };

  onMount(() => {
    updateViewport();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateViewport);
    }
    livingDialogManage.setPush((item) => {
      const id = ++toastId;
      // 添加新弹窗，isNeedZoomed 默认为 true
      const initialLoading = Boolean(item.ready);
      const itemWithDefaults = {
        ...item,
        isNeedZoomed: item.isNeedZoomed !== false, // 默认为 true
      };
      setContent((prev) => [...prev, { id, ...itemWithDefaults, loading: initialLoading, zoomed: false }]);
      requestAnimationFrame(clampAllDialogs);

      if (item.ready) {
        const readyPromise = typeof item.ready === 'function' ? item.ready() : item.ready;
        Promise.resolve(readyPromise)
          .catch(() => undefined)
          .finally(() => {
            setContent((list) => list.map((t) => (t.id === id ? { ...t, loading: false } : t)));
          });
      }
      return id;
    });
    livingDialogManage.setCloseAll(() => {
      setContent([]);
      animatedDialogs.clear();
    });
    livingDialogManage.setCloseById((id) => {
      removeContent(id);
    });
    livingDialogManage.setHideAll(() => {
      // setContent((list) => {
      //   const next = [...list];
      //   for (const t of next) {
      //     t.state = 'hidden'; // 直接改内部字段
      //   }
      //   console.log('hide all', next);
      //   return next; // 只创建一个新数组引用
      // });

      for (let i = 0; i < content.length; i++) {
        setContent(i, 'state', 'hidden');
      }
    });
    livingDialogManage.setRecoverAll(() => {
      // setContent((list) => {
      //   const next = [...list];
      //   for (const t of next) {
      //     t.state = 'visible'; // 直接改内部字段
      //   }
      //   return next; // 只创建一个新数组引用
      // });
      for (let i = 0; i < content.length; i++) {
        setContent(i, 'state', 'visible');
      }
    });
  });

  onCleanup(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', updateViewport);
    }
  });

  const removeContent = (id: number) => {
    setContent((list) => list.filter((t) => t.id !== id));
    animatedDialogs.delete(id); // 清理动画记录
  };

  return (
    <Portal ref={(el) => (el.className = 'livingDialog-layer')}>
      <div class="w-full h-full relative rounded">
        <For each={content}>
          {(item, index) => {
            let dialogRef!: HTMLDivElement;
            const isItemZoomed = () => item.zoomed ?? false;
            const dialogSize = () => getDialogSize(isItemZoomed());

            onMount(() => {
              playSlideInAnimation(dialogRef, item.id);
            });
            function handleZoom(isZoomed: boolean) {
              setContent(index(), 'zoomed', isZoomed);
              requestAnimationFrame(() => clampDialogIntoViewport(dialogRef));
            }
            return (
              <div
                ref={dialogRef}
                use:draggable={{
                  bounds: 'parent',
                  handle: `.living-dialog-header-${item.id}`,
                }}
                class="bg-layer3 absolute pointer-events-auto flex flex-col rounded-lg shadow"
                style={{
                  right: `${FIXED_RIGHT}px`,
                  bottom: `${FIXED_BOTTOM}px`,
                  width: `${dialogSize().width}px`,
                  'max-width': '100vw',
                  // height: `${dialogSize().height}px`,
                  'min-height': `${dialogSize().height}px`,
                  'max-height': '100vh',
                  display: item.state === 'hidden' ? 'none' : 'flex',
                  'box-shadow': '0 4px 20px 0 var(--alpha-neutral-black-a40, rgba(10, 15, 19, 0.40))',
                }}
                id={`living-dialog-${item.id}`}
              >
                <div
                  class={`living-dialog-header-${item.id} bg-layer5 h-11 flex items-center justify-between px-4  rounded-tl-lg rounded-tr-lg cursor-move`}
                >
                  <div>{item?.header && item?.header()}</div>
                  <div class="flex items-center">
                    <Show when={item.isNeedZoomed}>
                      <Show
                        when={isItemZoomed()}
                        fallback={
                          <IconVideoZoomIn
                            onClick={() => handleZoom(true)}
                            class="cursor-pointer text-neutral-main1_alt hover:text-brand-default size-[1.125rem] mr-2"
                          />
                        }
                      >
                        <IconVideoZoomOut
                          onClick={() => handleZoom(false)}
                          class="cursor-pointer text-neutral-main1_alt hover:text-brand-default size-[1.125rem] mr-2"
                        />
                      </Show>
                    </Show>

                    <div
                      onClick={() => {
                        removeContent(item.id);
                        item.closeCallback?.(item.id);
                      }}
                    >
                      <IconCloseMark class="cursor-pointer text-neutral-main1_alt hover:text-brand-default size-4" />
                    </div>
                  </div>
                </div>
                <div class="flex-1 min-h-0 flex">
                  <div class="flex-1 min-h-0 min-w-0 relative">
                    <Show when={item.loading}>
                      <div class="absolute inset-0 flex items-center justify-center rounded-b-lg">
                        <BarLoading class="text-white" />
                      </div>
                    </Show>
                    <div classList={{ 'opacity-0': item.loading }} class="w-full h-full">
                      {item.content()}
                    </div>
                  </div>
                </div>
                <Show when={item.footer}>
                  <div class="h-11 bg-layer7 rounded-bl-lg rounded-br-lg">{item.footer && item.footer()}</div>
                </Show>
              </div>
            );
          }}
        </For>
      </div>
    </Portal>
  );
}

// ========= 全局管理 =========
export const livingDialogManage = (() => {
  let push: ((item: Omit<ContentItem, 'id'>) => number | undefined) | null = null;
  let closeAll: (() => void) | null = null;
  let closeById: ((id: number) => void) | null = null;
  let hideAll: (() => void) | null = null;
  let recoverAll: (() => void) | null = null;

  return {
    setPush(fn: typeof push) {
      push = fn;
    },
    setCloseAll(fn: typeof closeAll) {
      closeAll = fn;
    },
    setCloseById(fn: typeof closeById) {
      closeById = fn;
    },
    setHideAll(fn: typeof hideAll) {
      hideAll = fn;
    },
    setRecoverAll(fn: typeof recoverAll) {
      recoverAll = fn;
    },
    show(item: Omit<ContentItem, 'id'>) {
      return push?.(item);
    },
    closeAll() {
      closeAll?.();
    },
    closeById(id: number) {
      closeById?.(id);
    },
    hideAll() {
      hideAll?.();
    },
    recoverAll() {
      recoverAll?.();
    },
  };
})();
